import React, { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export default function GalleryLightbox({ images, initialIndex, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    // Prevent body scrolling when lightbox is open
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [currentIndex]);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  // Touch swipe support for mobile
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrev();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col justify-between p-4 md:p-8 select-none">
      {/* Top bar */}
      <div className="flex justify-between items-center text-white z-10">
        <span className="text-sm font-semibold tracking-widest uppercase">
          {currentIndex + 1} / {images.length}
        </span>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/10 rounded-full transition-colors duration-150 cursor-pointer"
          aria-label="Luk"
        >
          <X className="w-8 h-8" />
        </button>
      </div>

      {/* Main viewport */}
      <div
        className="relative flex-1 flex items-center justify-center"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Previous Button */}
        <button
          onClick={handlePrev}
          className="absolute left-0 md:left-4 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all duration-150 z-10 cursor-pointer hidden md:block"
          aria-label="Forrige"
        >
          <ChevronLeft className="w-10 h-10" />
        </button>

        {/* Image Container */}
        <div className="max-w-full max-h-[80vh] flex items-center justify-center">
          <img
            src={images[currentIndex]}
            alt={`Galleri billede ${currentIndex + 1}`}
            className="max-w-full max-h-[80vh] object-contain shadow-2xl pointer-events-none"
          />
        </div>

        {/* Next Button */}
        <button
          onClick={handleNext}
          className="absolute right-0 md:right-4 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all duration-150 z-10 cursor-pointer hidden md:block"
          aria-label="Næste"
        >
          <ChevronRight className="w-10 h-10" />
        </button>
      </div>

      {/* Bottom bar / Swipe hint for mobile */}
      <div className="text-center text-gray-500 text-xs py-2 md:hidden">
        Svejp til venstre eller højre for at skifte billede
      </div>
    </div>
  );
}
